import { defineStore } from 'pinia';
import { GeographicalZone } from '@/types/geographicalZoneTypes.ts';
import type { User, UserApi } from '@/types/userTypes.ts';
import useHeaderStore from '@/stores/headerStore.ts';
import { computed, ref } from 'vue';
import { useAuth } from '@okta/okta-vue';
import { DateTime } from 'luxon';
import dateFormatConstants from '@/constants/dateFormatConstants.ts';
import workedDaysService from '@/services/workedDaysService.ts';
import userApiService from '@/services/userApiService.ts';
import timereportApiService from '@/services/timeReportApiService.ts';
import flatRateWorkApiService from '@/services/flatRateWorkApiService.ts';
import { TOAST_STATUS } from '@/types/toastTypes.ts';
import routesConstants from '@/constants/routesConstants.ts';
import type { Route } from '@/types/routesTypes.ts';
import userInfosService from '@/services/userInfosService.ts';
import messagesConstants from '@/constants/messagesConstants.ts';
import moment from 'moment';

export const useUserStore = defineStore('user', () => {
  // States
  const _user = ref<User>({
    gid: undefined,
    totalWorkedDays: undefined,
    name: '',
    email: '',
    gidEmail: '',
    flatRateWork: undefined,
    roles: {
      isExec: undefined,
      isManager: undefined,
      isHR: undefined,
    },
    zone: GeographicalZone.METROPOLE,
  });

  // Getters
  const user = computed(() => _user.value);

  // Actions
  async function refreshUserInfos() {
    if (!_user.value.gid) {
      const auth = useAuth();
      const userOktaInfos = await auth.getUser();

      if (userOktaInfos?.preferred_username) {
        const userGid = userInfosService.getGidFromEmail(userOktaInfos.preferred_username);

        _user.value.name = userOktaInfos.name || '';
        _user.value.email = userOktaInfos.email || '';
        _user.value.gidEmail = userOktaInfos.preferred_username;
        _user.value.gid = userGid;

        try {
          const todayDate = DateTime.now().toFormat(dateFormatConstants.DATE_FORMAT);
          const periodLimits = workedDaysService.getRangePeriod(todayDate);

          const newUser = await userApiService.getUserByGid(userGid);
          _user.value.zone = newUser.zone;

          await refreshUserTotalWorkedDays({
            userGid,
            startPeriod: periodLimits.startDate,
            endPeriod: periodLimits.endDate,
          });

          await refreshUserFlatRateWork({
            userGid,
            startPeriod: periodLimits.startDate,
            endPeriod: periodLimits.endDate,
          });

          await refreshUserRoles(newUser);
          await refreshUserAvailablePages(newUser);
        } catch (error) {
          const header = useHeaderStore();

          header.addToast({
            title: messagesConstants.USER_AUTHENTICATION_FAILED.TITLE,
            message: `${messagesConstants.USER_AUTHENTICATION_FAILED.MESSAGE} ${error}`,
            status: TOAST_STATUS.ERROR,
            isDisplayed: true,
            dismiss: true,
          });
        }

        return userGid;
      }
    }

    return _user.value.gid;
  }

  async function refreshUserTotalWorkedDays(payload: { userGid: string; startPeriod: string; endPeriod: string }) {
    const { userGid, startPeriod, endPeriod } = payload;
    const yearMonthStart = moment(startPeriod).format(dateFormatConstants.YEAR_MONTH_FORMAT);
    const yearMonthEnd = moment(endPeriod).format(dateFormatConstants.YEAR_MONTH_FORMAT);
    _user.value.totalWorkedDays = await timereportApiService.getTotalWorkedDays(userGid, yearMonthStart, yearMonthEnd);
  }

  async function refreshUserFlatRateWork(payload: { userGid?: string; startPeriod: string; endPeriod: string }) {
    const { userGid, startPeriod, endPeriod } = payload;
    try {
      if (userGid) {
        _user.value.flatRateWork = await flatRateWorkApiService.getFlatRateWorkByGid(userGid, startPeriod, endPeriod);
      }

      return _user.value.flatRateWork;
    } catch {
      const headerStore = useHeaderStore();

      headerStore.addToast({
        title: messagesConstants.GET_FLAT_RATE_WORK_FAILED.TITLE,
        message: messagesConstants.GET_FLAT_RATE_WORK_FAILED.toParamString(userGid),
        status: TOAST_STATUS.ERROR,
        dismiss: true,
        isDisplayed: true,
      });

      return undefined;
    }
  }

  async function refreshUserRoles(newUser: UserApi) {
    _user.value.roles = {
      isExec: newUser.exec,
      isHR: newUser.hr,
      isManager: newUser.manager,
    };

    return _user.value.roles;
  }

  async function refreshUserAvailablePages(newUser: UserApi) {
    const userAvailablePages: Route[] = [];

    if (newUser.exec) {
      userAvailablePages.push(routesConstants.EXECUTIVE);
    }

    if (newUser.manager) {
      userAvailablePages.push(routesConstants.MANAGER);
    }

    if (newUser.hr) {
      userAvailablePages.push(routesConstants.HR);
    }

    _user.value.availablePages = userAvailablePages;

    return _user.value.availablePages;
  }

  return {
    user,
    refreshUserInfos,
    refreshUserFlatRateWork,
    refreshUserTotalWorkedDays,
  };
});

export default useUserStore;
